export interface PromptSummary {
  id: string
  title: string
  description: string
  price: number
  originalPrice?: number | null
  category: string
  rating: number
  reviewCount: number
  author: string
  thumbnail: string
  isPremium?: boolean
  tags?: string[]
  /** Supabase 등 서버에서 넣을 때만 사용(정렬 등) */
  createdAt?: string
}

export interface PromptReview {
  id: string
  author: string
  rating: number
  date: string
  content: string
  helpful: number
}

export interface PromptDetail extends PromptSummary {
  longDescription: string
  downloadCount: number
  viewCount: number
  images: string[]
  features: string[]
  reviews: PromptReview[]
  authorProfile: {
    name: string
    avatar: string
    rating: number
    promptCount: number
    totalSales: number
    joinDate: string
  }
}

export const promptSummaries: PromptSummary[] = [
  {
    id: "1",
    title: "ChatGPT 마케팅 자료 생성 프롬프트",
    description: "광고 카피부터 SNS 콘텐츠까지 ChatGPT를 활용한 마케팅 자료 생성 프롬프트입니다.",
    price: 25000,
    originalPrice: 32000,
    category: "마케팅",
    rating: 4.9,
    reviewCount: 2158,
    author: "AI마케터",
    thumbnail: "/marketing-copywriting.jpg",
    isPremium: true,
    tags: ["ChatGPT", "마케팅", "카피라이팅"],
  },
  {
    id: "2",
    title: "광고 문구 생성 프롬프트",
    description: "효과적인 광고 문구를 생성하는 AI 프롬프트 모음집입니다.",
    price: 20000,
    category: "광고",
    rating: 4.8,
    reviewCount: 1890,
    author: "광고전문가",
    thumbnail: "/marketing-copywriting.jpg",
    isPremium: false,
    tags: ["광고", "카피라이팅", "AI"],
  },
  {
    id: "3",
    title: "마케팅 카피라이팅 프롬프트",
    description: "매출을 높이는 효과적인 마케팅 카피를 작성할 수 있는 프롬프트 모음집입니다.",
    price: 18000,
    category: "마케팅",
    rating: 4.6,
    reviewCount: 1567,
    author: "마케팅전문가",
    thumbnail: "/blog-writing-content.jpg",
    tags: ["마케팅", "카피라이팅", "광고"],
  },
  {
    id: "4",
    title: "AI 이미지 생성 마스터 프롬프트",
    description: "Midjourney, DALL-E, Stable Diffusion 등 AI 이미지 생성 도구를 위한 전문 프롬프트 모음집입니다.",
    price: 15000,
    category: "이미지 생성",
    rating: 4.8,
    reviewCount: 1234,
    author: "프롬프트마스터",
    thumbnail: "/marketing-copywriting.jpg",
    tags: ["Midjourney", "DALL-E", "이미지"],
  },
  {
    id: "5",
    title: "온라인 마케팅 대화 프롬프트",
    description: "고객과의 효과적인 온라인 마케팅 대화를 위한 AI 대화 프롬프트입니다.",
    price: 10000,
    category: "온라인",
    rating: 4.5,
    reviewCount: 756,
    author: "온라인마케터",
    thumbnail: "/code-review-programming.jpg",
    tags: ["온라인", "마케팅", "대화"],
  },
  {
    id: "6",
    title: "한국 소설 아이디어 생성기",
    description: "독창적인 소설 아이디어와 캐릭터를 생성하는 창작 전용 프롬프트입니다.",
    price: 12000,
    category: "창작",
    rating: 4.7,
    reviewCount: 987,
    author: "작가김씨",
    thumbnail: "/blog-writing-content.jpg",
    tags: ["소설", "창작", "아이디어"],
  },
]

export const homePromptIds = ["4", "1", "6", "3"]

export const promptDetails: Record<string, PromptDetail> = {
  "1": {
    ...promptSummaries[0],
    longDescription: `이 프롬프트 패키지는 마케팅 전문가들이 실제로 사용하는 고품질 카피라이팅 프롬프트들을 모은 것입니다.

포함된 내용:
• 제품 소개 카피 생성 프롬프트 (20개)
• 이메일 마케팅 프롬프트 (15개)
• 소셜미디어 광고 카피 프롬프트 (25개)
• 랜딩페이지 헤드라인 프롬프트 (10개)
• CTA 버튼 문구 생성 프롬프트 (12개)

각 프롬프트는 즉시 사용 가능한 형태로 제공되며, 다양한 산업에서 재사용할 수 있도록 설계했습니다.`,
    downloadCount: 1250,
    viewCount: 5420,
    images: ["/marketing-copywriting.jpg", "/marketing-sample-1.jpg", "/marketing-sample-2.jpg"],
    features: ["82개의 검증된 프롬프트", "산업별 맞춤 템플릿", "사용 가이드 포함", "평생 업데이트 지원", "30일 환불 보장"],
    reviews: [
      {
        id: "1",
        author: "박구매자",
        rating: 5,
        date: "2024년 1월 15일",
        content: "실제로 사용해보니 카피 품질이 확실히 좋아졌습니다.",
        helpful: 12,
      },
      {
        id: "2",
        author: "이마케터",
        rating: 4,
        date: "2024년 1월 10일",
        content: "가격 대비 만족스럽고 이메일 마케팅 프롬프트가 특히 유용했습니다.",
        helpful: 8,
      },
    ],
    authorProfile: {
      name: "김마케터",
      avatar: "/author-avatar.jpg",
      rating: 4.9,
      promptCount: 23,
      totalSales: 15600,
      joinDate: "2023년 3월",
    },
  },
}

export const getPromptById = (id: string) => promptSummaries.find((prompt) => prompt.id === id)

export const getPromptDetailById = (id: string): PromptDetail | undefined => {
  const detailed = promptDetails[id]
  if (detailed) return detailed

  const summary = getPromptById(id)
  if (!summary) return undefined

  return {
    ...summary,
    longDescription: `${summary.description}\n\n이 상세 정보는 목업 데이터에서 자동 생성된 기본 설명입니다.`,
    downloadCount: summary.reviewCount * 3,
    viewCount: summary.reviewCount * 12,
    images: [summary.thumbnail],
    features: ["즉시 사용 가능한 템플릿", "상황별 활용 예시 포함", "지속 업데이트 예정"],
    reviews: [],
    authorProfile: {
      name: summary.author,
      avatar: "/placeholder.svg",
      rating: summary.rating,
      promptCount: 1,
      totalSales: summary.reviewCount * 2,
      joinDate: "2024년 1월",
    },
  }
}
